import React, { useMemo } from "react";
import _ from "lodash";
import cx from "classnames";

import Slider from "src/components/slider";
import { DocusignEnvelopeRecipient } from "src/types/integrations/docusign";

import styles from "./styles.module.scss";

const DefaultProfilePicture = "/images/default_user_profile_background.png";

interface ESignStatusProps {
  asset: {
    id?: string;
    name?: string;
    metadata?: {
      url?: string;
      docusign?: {
        recipients?: DocusignEnvelopeRecipient[];
      };
    };
  };
  onClose: () => void;
}

const ESignStatus: React.FC<ESignStatusProps> = ({ asset, onClose }) => {
  const docusignUrl = _.get(asset, "metadata.url", "");
  const assetName = _.get(asset, "name", "document");

  const signers = useMemo(() => {
    const recipients = _.get(asset, "metadata.docusign.recipients", []) as DocusignEnvelopeRecipient[];
    return recipients.filter(
      (recipient) =>
        !recipient.recipientType ||
        recipient.recipientType === "signer",
    );
  }, [asset]);

  const handleOpenDocusign = () => {
    if (docusignUrl) {
      window.open(docusignUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className={styles.container}>
      <Slider.Header
        title={
          <div className={styles.header}>
            <div className={styles.headerText}>
              <h2 className={styles.title}>eSign status</h2>
              <p className={styles.subtitle}>
                Keep track of each recipient&apos;s eSign status.
              </p>
            </div>
            <i
              className={cx("paperflite-close", styles.closeIcon)}
              onClick={onClose}
              role="button"
              aria-label="Close"
            />
          </div>
        }
      />
      <Slider.Body>
        {docusignUrl && (
          <button
            type="button"
            className={styles.openDocusign}
            onClick={handleOpenDocusign}
          >
            <p className={styles.openDocusignText}>
              Open &quot;{assetName}&quot; on Docusign
            </p>
            <i className={cx("paperflite-open", styles.openDocusignIcon)} />
          </button>
        )}

        <p className={styles.sectionLabel}>Recipient&apos;s list</p>

        {_.isEmpty(signers) ? (
          <p className={styles.empty}>No recipients found.</p>
        ) : (
          <div className={styles.recipientList}>
            {signers.map((recipient) => {
              const key =
                recipient.recipientId ||
                recipient.email ||
                recipient.name;

              return (
                <div key={key} className={styles.recipientRow}>
                      <img className={styles.avatar} src={DefaultProfilePicture} alt="Profile" />
                  <div className={styles.recipientInfo}>
                    <p className={styles.recipientName}>
                      {recipient.name || recipient.email || "—"}
                    </p>
                    {recipient.email && recipient.name && (
                      <p className={styles.recipientEmail}>{recipient.email}</p>
                    )}
                  </div>
                  <span
                    className={cx(styles.statusBadge, {
                      [styles.statusSigned]: recipient.status === "completed"
                    })}
                  >
                    {recipient.status}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Slider.Body>
    </div>
  );
};

export default ESignStatus;
